import { inject } from '@angular/core';
import { Router, CanActivateFn, RouterStateSnapshot } from '@angular/router';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { from, of } from 'rxjs';
import { switchMap, take } from 'rxjs/operators';
import { AppConfigService } from '../services/app-config.service';
import { DemoModeService } from '../services/demo-mode.service';
import { SystemAccessService } from '../services/system-access.service';

/**
 * The paused faces' door on the portal's two nodes (the root and a section drill-in). While the brand's public faces are
 * paused — the anonymous config's `publicFacesPaused`, the server's one word: this guard reads no plan state and compares
 * no date — a caller the brand does not admit is sent to the paused page with the address they wanted, and an admitted
 * member or a root reads on. Composed AFTER the portal root's own guard (which sends members home and walls a paused
 * member) and alone on the drill-in, which carries no other guard because members see the same public view there.
 */
export const publicPausedGuard: CanActivateFn = (_route, state: RouterStateSnapshot) => {
  const appConfig = inject(AppConfigService);
  if (appConfig.config$()?.publicFacesPaused !== true) {
    return of(true);
  }

  const demoModeService = inject(DemoModeService);
  if (demoModeService.isDemo()) {
    return of(true);
  }

  const router = inject(Router);
  const oidcSecurityService = inject(OidcSecurityService);
  const systemAccessService = inject(SystemAccessService);
  const toThePausedPage = () => router.createUrlTree(['/paused'], { queryParams: { returnUrl: state.url } });

  return oidcSecurityService.isAuthenticated$.pipe(
    take(1),
    switchMap(({ isAuthenticated }) => {
      if (!isAuthenticated) {
        return of(toThePausedPage());
      }
      // The root leg must be RESOLVED before isMember() / isRoot() are trusted on a cold load.
      return from(systemAccessService.getPrivilegeLevel()).pipe(
        switchMap(async () => (systemAccessService.isMember() || systemAccessService.isRoot()) ? true : toThePausedPage()),
      );
    }),
  );
};
